import { SearchOutlined } from '@ant-design/icons'
import { ReactNode } from 'react'
import { Field } from 'react-final-form'
import { useTranslation } from 'react-i18next'
import styled from 'styled-components'

import { useViewportSize } from '../../hooks'
import { composeValidators, emptyOrLength } from '../../utils/validation'
import { Button } from '../Button'
import { Flex } from '../Flex'
import { FormControl } from '../FormControl'
import { Input } from '../Input'
import { Space, SpaceProps } from '../Space'
import { themeSpace } from '../theme'

const StyledQuickFilter = styled(Space)`
  gap: ${themeSpace(3)} 0;
`

export interface QuickFilterProps {
  searchLabel?: string
  searchPlaceholder?: string
  searchName: string
  searchNote?: string
  width?: SpaceProps['width']
  additionalAction?: ReactNode
  validateMin3?: boolean
}

export const QuickFilter = ({
  searchLabel,
  searchPlaceholder,
  searchName,
  searchNote,
  width,
  additionalAction,
  validateMin3 = true
}: QuickFilterProps) => {
  const { t } = useTranslation(['form'])
  const viewPortSize = useViewportSize()
  const isMobile = viewPortSize?.isMobile ?? false

  return (
    <Space width="100%" justifyContent="space-between" flexDirection="column" direction="vertical" space={3}>
      <StyledQuickFilter
        direction="horizontal"
        width={width || [1, 6 / 12, 8 / 12]}
        alignItems="flex-end"
        space={2}
        flexWrap="wrap"
        data-automation-id="transactionsQuickFilter"
      >
        <Space direction="horizontal" flexDirection="row" alignItems="flex-end" flexGrow={1}>
          <Field
            name={searchName}
            validate={validateMin3 ? composeValidators(emptyOrLength({ min: 3 })) : undefined}
            render={fieldProps => (
              <FormControl
                {...fieldProps}
                component={Input}
                hideValidationMessage
                size="middle"
                label={searchLabel}
                placeholder={searchPlaceholder}
                suffix={<SearchOutlined color="primary" />}
                background={'lightestGray'}
              />
            )}
          />

          <Button htmlType="submit" variant="secondary">
            {t('form:Search')}
          </Button>
        </Space>
        {!isMobile && additionalAction}
      </StyledQuickFilter>
      {isMobile && additionalAction && (
        <Flex width={1} justifyContent="center">
          {additionalAction}
        </Flex>
      )}
      {searchNote && <Flex width={1}>{searchNote}</Flex>}
    </Space>
  )
}
